import { all, takeEvery, call, put, take, select } from 'redux-saga/effects'
import { push } from 'connected-react-router'
import actionTypes from '../../actions/actionTypes'
import createApiCall, { apiURL } from '../../apis/wpApi/endpoints'
import authUser from '../../apis/wpApi/authUser'
import { OVERLAY_SPINNER } from '../../constants/overlayTypes'
import getData from '../../functions/getData'
import { toStartingDateString } from '../../functions/pmStartingDate'
import { ORIGIN_PM } from '../../constants/metaMovieOrigins'
import {
    PREVIEW_MOVIES_ITEMS,
    PROGRAM_CLI_MOVIES_ITEMS,
    PROGRAM_MOVIEMAPPING_ITEMS,
    PROGRAM_PM_MOVIES_ITEMS,
    PROGRAM_META_MOVIES_ITEMS,
    TRANSLATION,
} from '../../constants/state'

const createPreviewMovieCall = (data) => createApiCall({
    url:          apiURL.PM_PREVIEW_MOVIES_CREATE,
    method:       'POST',
    data,
    headerParams: { token: authUser.getToken() },
})

const metaMoviePkSelector = (state, previewPk) => getData(state, [PREVIEW_MOVIES_ITEMS, previewPk, 'metaMoviePk'])
const shownStartingDateSelector = (state, metaMoviePk) => getData(state, [PROGRAM_CLI_MOVIES_ITEMS, metaMoviePk, 'startingDate'])
const pmMoviePkSelector = (state, metaMoviePk) => getData(state, [PROGRAM_MOVIEMAPPING_ITEMS, metaMoviePk, 'pm'])
const pmMovieSelector = (state, pmMoviePk) => getData(state, [PROGRAM_PM_MOVIES_ITEMS, pmMoviePk])
const metaMovieSelector = (state, metaMoviePk) => getData(state, [PROGRAM_META_MOVIES_ITEMS, metaMoviePk])
const timezoneSelector = state => state.getIn([TRANSLATION, 'timezone'])

// The popup shows the movie's effective start date (cliMovies). Changing it does
// what the movie edit page does: the pm row takes the new day and the
// starting_date selector moves to Pmkino, so MCS serves that day. An untouched
// date writes neither (#112). Both sides are compared as days, because until
// the next sync cliMovies can hold a Date or a pm timestamp from the movie
// edit form. Without a pm or meta row there is nothing to write to, and the
// preview row still saves (same guard as applyKeptTitle).
function* writeStartingDate(previewPk, expectedStartDate) {
    const timezone = yield select(timezoneSelector)
    const metaMoviePk = yield select(metaMoviePkSelector, previewPk)
    const newDay = toStartingDateString(expectedStartDate, timezone)
    const shownDay = toStartingDateString(yield select(shownStartingDateSelector, metaMoviePk), timezone)
    if (newDay === shownDay) return

    const pmMoviePk = yield select(pmMoviePkSelector, metaMoviePk)
    const pmMovie = pmMoviePk ? yield select(pmMovieSelector, pmMoviePk) : null
    const metaMovie = yield select(metaMovieSelector, metaMoviePk)
    if (!pmMovie || !metaMovie) return

    yield put({
        type: actionTypes.PMMOVIES_UPDATE_ITEM_STARTING_DATE,
        payload: { pk: String(pmMoviePk), startingDate: newDay, timezone },
    })
    yield put({
        type: actionTypes.METAMOVIES_UPDATE_ITEM_ORIGIN,
        payload: { pk: metaMoviePk, dataMeta: { ...metaMovie.toJS(), starting_date: ORIGIN_PM } },
    })
}

function* watchCreate() {
    yield takeEvery(actionTypes.PREVIEW_MOVIES_CREATE_REQUEST, function*(action) {
        try {
            const { redirectTo, ...createData } = action.payload
            yield put({ type: actionTypes.UI_GENERAL_REQUEST_OVERLAY_SET_REQUEST_TYPE, payload: { requestType: actionTypes.PREVIEW_MOVIES_CREATE_REQUEST } })
            yield put({ type: actionTypes.UI_GENERAL_REQUEST_OVERLAY_SET_OVERLAY_TYPE, payload: { overlayType: OVERLAY_SPINNER } })
            const response = yield call(createPreviewMovieCall, createData)
            yield put({
                type: actionTypes.PREVIEW_MOVIES_CREATE_SUCCESS,
                payload: { item: response.data },
            })
            yield put({ type: actionTypes.PROGRAM_GET_REQUEST, payload: { signal: false } })
            yield take([actionTypes.PROGRAM_GET_SUCCESS, actionTypes.PROGRAM_GET_ERROR])
            yield put({ type: actionTypes.UI_GENERAL_REQUEST_OVERLAY_RESET })
            const pk = response.data.metaMoviePk
            if (redirectTo === 'tmdb') {
                yield put(push(`/tmdb/${pk}?from=previewmovies`))
            } else {
                yield put(push(`/website/previewMovies/movie/${pk}`))
            }
        } catch (e) {
            yield put({ type: actionTypes.PREVIEW_MOVIES_CREATE_FAILURE })
        }
    })
}

function* watchUpdate() {
    yield takeEvery(actionTypes.PREVIEW_MOVIES_UPDATE_ITEM, function*(action) {
        yield call(writeStartingDate, action.payload.pk, action.payload.expectedStartDate)
        yield put({
            type: actionTypes.PROGRAM_UPDATE_ITEM_UPDATE,
            payload: { pk: action.payload.pk, objectType: 'pmPreviewMovies' },
        })
        yield put({
            type: actionTypes.PROGRAM_UPDATE_REQUEST,
            payload: { beforeUpdate: false },
        })
    })
}

function* watchDelete() {
    yield takeEvery(actionTypes.PREVIEW_MOVIES_DELETE_REQUEST, function*(action) {
        yield put({
            type: actionTypes.PROGRAM_UPDATE_ITEM_DELETE,
            payload: { pk: action.payload.pk, objectType: 'pmPreviewMovies' },
        })
        yield put({
            type: actionTypes.PREVIEW_MOVIES_DELETE_SUCCESS,
            payload: { pk: action.payload.pk },
        })
        yield put({
            type: actionTypes.PROGRAM_UPDATE_REQUEST,
            payload: { beforeUpdate: false },
        })
    })
}

export default function* previewMoviesSaga() {
    yield all([
        watchCreate(),
        watchUpdate(),
        watchDelete(),
    ])
}
